import { type JSX, useCallback, useEffect, useState } from "react"

import { PurchaseOrderTabs } from "~/components/planning/purchaseOrderComponents/CreatePurchaseOrderTabs"
import { Button } from "~/components/reusable/Button"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import { trackEvent } from "~/hooks/useTrackingService"
import { useVariantsByIds } from "~/hooks/useVariants"
import { Invoice } from "@phosphor-icons/react"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { getCommonVendors } from "~/utils/vendorUtils"
import { CreateMultiplePurchaseOrders } from "~/components/planning/purchaseOrderComponents/CreateMultiplePurchaseOrders"
import { EMPTY_ARRAY } from "~/utils/tsUtils"

type PurchaseOrderDialogProps = {
  customTrigger?: JSX.Element | React.ReactNode
  triggerButtonVariant?: "neutral" | "primary"
  variantIds?: string[]
  customOpen?: boolean
  onClose?: () => void
  preselectedVendorId?: string
  variants?: IVariantSummary[]
  start?: Date
  end?: Date
  preSelectedWarehouseId?: string
  onCreate?: () => void
  collectionIds?: string[]
  /** Context (e.g. product name) used to pre-populate the purchase order name */
  nameContext?: string
}

/**
 * A dialog for creating purchase orders.
 * This component can be used in two ways:
 * 1. With variantIds: The component will fetch the variants and display them
 * 2. With variants: The component will use the provided variants directly
 */
export function PurchaseOrderDialog({
  customTrigger,
  triggerButtonVariant = "neutral",
  variantIds,
  customOpen,
  onClose,
  preselectedVendorId,
  variants: providedVariants,
  start,
  end,
  preSelectedWarehouseId,
  onCreate,
  collectionIds,
  nameContext,
}: PurchaseOrderDialogProps) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (customOpen !== undefined) {
      setOpen(customOpen)
    }
  }, [customOpen])

  const handleOpenChange = (open: boolean) => {
    if (open) {
      trackEvent("Purchase Order Dialog Opened")
    }

    setOpen(open)

    if (!open) {
      onClose?.()
    }
  }

  const handleEdit = () => {
    onCreate?.()
    setOpen(false)
  }

  const closeDialog = useCallback(() => setOpen(false), [])

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger requires="edit" asChild>
        {customTrigger ? (
          customTrigger
        ) : (
          <Button variant={triggerButtonVariant} size="sm">
            <Invoice />
            Reorder
          </Button>
        )}
      </DialogTrigger>
      {open && (
        <PurchaseOrderDialogContent
          variantIds={variantIds}
          providedVariants={providedVariants}
          preselectedVendorId={preselectedVendorId}
          start={start}
          end={end}
          preSelectedWarehouseId={preSelectedWarehouseId}
          onEdit={handleEdit}
          onClose={closeDialog}
          collectionIds={collectionIds}
          nameContext={nameContext}
        />
      )}
    </DialogRoot>
  )
}

type PurchaseOrderDialogContentProps = {
  variantIds?: string[]
  providedVariants?: IVariantSummary[]
  preselectedVendorId?: string
  start?: Date
  end?: Date
  preSelectedWarehouseId?: string
  onEdit: () => void
  onClose: () => void
  collectionIds?: string[]
  nameContext?: string
}

function PurchaseOrderDialogContent({
  variantIds,
  providedVariants,
  preselectedVendorId,
  start,
  end,
  preSelectedWarehouseId,
  onEdit,
  onClose,
  collectionIds,
  nameContext,
}: PurchaseOrderDialogContentProps) {
  const fetchedVariants = useVariantsByIds(variantIds || EMPTY_ARRAY)
  const variants = providedVariants || fetchedVariants

  const selectableVendors = getCommonVendors(variants)
  const multipleVendors = selectableVendors.length === 0

  return (
    <DialogContentV2 className="min-w-[740px]">
      <DialogHeaderV2>
        <DialogTitle>
          {multipleVendors ? "Create Multiple Purchase Orders" : "Handle Purchase Order"}
        </DialogTitle>
      </DialogHeaderV2>
      <DialogBodyV2>
        {multipleVendors ? (
          <CreateMultiplePurchaseOrders
            variants={variants}
            start={start}
            end={end}
            preSelectedWarehouseId={preSelectedWarehouseId}
            onCreate={onEdit}
            onClose={onClose}
            collectionIds={collectionIds}
          />
        ) : (
          <PurchaseOrderTabs
            variants={variants}
            vendors={selectableVendors}
            start={start}
            end={end}
            preSelectedVendorId={preselectedVendorId}
            preSelectedWarehouseId={preSelectedWarehouseId}
            onEdit={onEdit}
            onCancel={onClose}
            collectionIds={collectionIds}
            nameContext={nameContext}
          />
        )}
      </DialogBodyV2>
      {/* Empty footer because we portal in the elements from the content children */}
      <DialogFooterV2 id={"purchaseOrderDialogFooter"} />
    </DialogContentV2>
  )
}
